import type { Metric } from "web-vitals";
import * as Sentry from "@sentry/react";
import { trackEvent } from "./posthog";

/**
 * Web Vitals Monitoring
 *
 * Tracks Core Web Vitals and sends them to:
 * - Sentry: Performance measurements + breadcrumbs + alerts for "poor" ratings
 * - PostHog: Custom events for analytics dashboard
 * - LogRocket: Session tracking (if configured)
 *
 * Metrics tracked:
 * - LCP (Largest Contentful Paint): Loading performance — good < 2.5s
 * - INP (Interaction to Next Paint): Responsiveness — good < 200ms
 * - CLS (Cumulative Layout Shift): Visual stability — good < 0.1
 * - FCP (First Contentful Paint): Initial render — good < 1.8s
 * - TTFB (Time to First Byte): Server response time — good < 800ms
 */

interface WebVitalsConfig {
  enableSentry?: boolean;
  enablePostHog?: boolean;
  enableLogRocket?: boolean;
  enableConsole?: boolean;
}

const defaultConfig: WebVitalsConfig = {
  enableSentry: true,
  enablePostHog: true,
  enableLogRocket: true,
  enableConsole: import.meta.env.DEV,
};

/**
 * Send metric to all monitoring services
 */
function sendToMonitoring(metric: Metric, config: WebVitalsConfig) {
  const { name, value, rating, delta } = metric;

  // Log to console in development
  if (config.enableConsole) {
    const emoji = rating === "good" ? "✅" : rating === "needs-improvement" ? "⚠️" : "❌";
    console.log(`[Web Vitals] ${emoji} ${name}:`, {
      value: Math.round(value),
      rating,
      delta: Math.round(delta),
    });
  }

  // ─── Sentry ───────────────────────────────────────────────
  if (config.enableSentry && import.meta.env.VITE_SENTRY_DSN) {
    // Record as performance measurement (shows in Sentry Performance tab)
    Sentry.setMeasurement(name, value, name === "CLS" ? "none" : "millisecond");

    // Add breadcrumb so every error has Web Vitals context
    Sentry.addBreadcrumb({
      category: "web-vitals",
      message: `${name}: ${name === "CLS" ? value.toFixed(3) : Math.round(value) + "ms"} (${rating})`,
      level: rating === "good" ? "info" : rating === "needs-improvement" ? "warning" : "error",
      data: {
        value: Math.round(value),
        rating,
        delta: Math.round(delta),
        page: window.location.pathname,
      },
    });

    // Send Sentry event for "poor" performance — creates an issue in Sentry dashboard
    if (rating === "poor") {
      Sentry.captureMessage(`[Web Vitals] Poor ${name}: ${name === "CLS" ? value.toFixed(3) : Math.round(value) + "ms"}`, {
        level: "warning",
        tags: {
          web_vital: name,
          rating,
          page: window.location.pathname,
        },
        extra: {
          value: Math.round(value),
          delta: Math.round(delta),
          is_mobile: window.innerWidth < 768,
          device_width: window.innerWidth,
        },
      });
    }
  }

  // ─── PostHog ──────────────────────────────────────────────
  if (config.enablePostHog && import.meta.env.VITE_POSTHOG_KEY) {
    trackEvent("web_vital", {
      metric_name: name,
      value: Math.round(value),
      rating,
      page: window.location.pathname,
      is_mobile: window.innerWidth < 768,
      device_width: window.innerWidth,
    });
  }

  // ─── LogRocket ────────────────────────────────────────────
  if (config.enableLogRocket && import.meta.env.VITE_LOGROCKET_APP_ID) {
    void import("logrocket")
      .then(({ default: LogRocket }) => {
        LogRocket.track(`WebVital:${name}`, {
          value: Math.round(value),
          rating,
          delta: Math.round(delta),
        });
      })
      .catch(() => {
        // Observability must never interrupt an operator-facing workflow.
      });
  }
}

/**
 * Initialize Web Vitals monitoring
 * Call once in main.tsx after initSentry() and initPostHog()
 */
export function initWebVitals(config: WebVitalsConfig = defaultConfig) {
  const finalConfig = { ...defaultConfig, ...config };

  // Optional telemetry must load after the application shell. A missing or
  // stale optimized dependency must never leave the entire operator UI blank.
  void import("web-vitals")
    .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
      onCLS(metric => sendToMonitoring(metric, finalConfig));
      onLCP(metric => sendToMonitoring(metric, finalConfig));
      onFCP(metric => sendToMonitoring(metric, finalConfig));
      onTTFB(metric => sendToMonitoring(metric, finalConfig));
      onINP(metric => sendToMonitoring(metric, finalConfig));
      console.log("[Web Vitals] Monitoring initialized → Sentry + PostHog");
    })
    .catch(() => {
      if (import.meta.env.DEV) {
        console.warn("[Web Vitals] Optional telemetry was unavailable; application rendering continues.");
      }
    });
}

/**
 * Web Vitals thresholds (from Google Core Web Vitals spec)
 */
export const WEB_VITALS_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
  INP: { good: 200, needsImprovement: 500 },
};
